import { EMPTY_STRING, JoinChar } from '@common/constants';
import { COLOR_BLACK } from '@common/constants/colors';
import { MapMarkerSelectType, MarkerPlanning, MarkerType } from '@common/types';
import { MarkerDataAttribute, MarkerFigure, MarkerFigureWidth } from '@common/types/components/map/map';
import { MonitoringSensorEventStatusType } from '@common/types/dictionaries/monitoring-waypoint';
import cn from 'classnames';
import L from 'leaflet';
import { isNil } from 'lodash';
import marker from './icons/location.svg';

export const defaultMarker = new L.Icon({
  iconUrl: marker,
  iconSize: new L.Point(20, 35)
});

const markerIcon = (color: string) => {
  return (
    '<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg"xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"viewBox="0 0 392.436 392.436" style="enable-background:new 0 0 392.436 392.436;" xml:space="preserve">' +
    '<path style="fill:#fff;" d="M196.186,21.786c-65.228,0-118.303,53.075-118.303,118.303c0,51.459,78.675,169.309,118.303,223.418c39.628-54.109,118.303-172.024,118.303-223.418C314.489,74.861,261.414,21.786,196.186,21.786z"/>' +
    '<g><path style="fill:' +
    color +
    ';" d="M196.186,0C118.933,0,56.097,62.836,56.097,140.089c0,74.149,126.061,241.196,131.426,248.178c4.073,5.624,13.188,5.495,17.39,0c5.301-7.111,131.426-174.028,131.426-248.178C336.275,62.836,273.438,0,196.186,0zM77.883,140.089c0-65.228,53.075-118.303,118.303-118.303s118.303,53.075,118.303,118.303c0,51.394-78.675,169.309-118.303,223.418C156.558,309.398,77.883,191.547,77.883,140.089z"/></g>' +
    '</svg>'
  );
};

export const customMarker = (icon: string, color: string) => {
  color = color ?? '#4C91FF';

  const html = '<div class="custom-marker">' + markerIcon(color) + '<img src="' + icon + '" class="icon home" alt="icon" />' + '</div>';

  return new L.DivIcon({
    iconSize: new L.Point(28, 35),
    className: 'my-div-icon',
    html
  });
};

export const destinationMarker = (text: string, color: string) => {
  color = color ?? '#4C91FF';

  const html = '<div class="custom-marker text">' + markerIcon(color) + '<span class="text">' + text + '</div>' + '</div>';

  return new L.DivIcon({
    iconSize: new L.Point(30, 38),
    className: 'my-div-icon',
    html: html
  });
};

export const orderMarker = (color: string, selected: boolean | string, id: string | number, data: any) => {
  const dataJSON = JSON.stringify(data);

  const html = L.DomUtil.create('div', cn('marker-order', { selected }));
  html.style.borderColor = color;
  html.id = 'marker_' + id.toString();
  html.setAttribute(MarkerDataAttribute.MarkerType, MarkerType.Order);
  html.setAttribute(MarkerDataAttribute.OrderID, id.toString());
  html.innerHTML = dataJSON;

  return new L.DivIcon({
    iconSize: new L.Point(24, 24),
    className: 'my-div-icon',
    html: html
  });
};

const createTriangleSVG = ({ color, strokeDasharray, width }: { color: string; strokeDasharray: string; width: number }) => {
  return (
    '<svg width="48px" height="48px" fill="#fff" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">' +
    '<path stroke="' +
    color +
    '" ' +
    strokeDasharray +
    'stroke-width="' +
    width +
    '" ' +
    'fill-rule="evenodd" clip-rule="evenodd" d="M22.2692 6.98965C23.0395 5.65908 24.9605 5.65908 25.7309 6.98965L44.262 38.9979C45.0339 40.3313 44.0718 42 42.5311 42H5.4689C3.92823 42 2.96611 40.3313 3.73804 38.9979L22.2692 6.98965Z" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>'
  );
};
const pointMarkerFigure = ({
  figure,
  selected,
  color,
  strokeDasharray,
  width,
  number,
  id,
  waypointID
}: {
  figure: MarkerFigure | undefined;
  selected: MapMarkerSelectType | string | undefined;
  color: string;
  strokeDasharray: string;
  number: number | string;
  id: number | string;
  width: number;
  waypointID?: number;
}) => {
  const classFigure = figure ? { [figure]: figure } : {};
  const classSelected = selected ? { [selected]: selected } : {};
  const figureIcon = L.DomUtil.create('div', cn('marker-point', classSelected, classFigure));
  const widthT = selected ? 8 : width;
  const triangleSVG = createTriangleSVG({ color, strokeDasharray, width: widthT });

  if (figure === MarkerFigure.Triangle) {
    figureIcon.innerHTML = triangleSVG + number;
  } else if (!figure) {
    figureIcon.innerHTML = markerIcon(color) + number;
    figureIcon.style.borderWidth = `${width / 2}px`;
  } else {
    figureIcon.innerHTML = number.toString();
    if (selected === MapMarkerSelectType.PartiallySelected) {
      figureIcon.style.borderStyle = 'dashed';
    } else {
      figureIcon.style.borderStyle = 'solid';
    }
    figureIcon.style.borderWidth = `${widthT / 2}px`;
  }
  figureIcon.setAttribute('id', `marker_${id}`);
  if (!isNil(waypointID)) {
    figureIcon.setAttribute(MarkerDataAttribute.WaypointID, waypointID.toString());
  }
  figureIcon.style.borderColor = color;
  return figureIcon;
};
export const pointMarker = (
  data: MarkerPlanning['data'],
  color: string,
  number: number | string,
  figure: MarkerFigure | undefined,
  selected: MapMarkerSelectType | string | boolean | undefined,
  id: string | number,
  isHome?: number | boolean | null,
  homeNumber?: number,
  width: number = 4
) => {
  color = selected ? '#FFA605' : color ?? 'red';
  if (selected === false || selected === undefined) {
    selected = MapMarkerSelectType.None;
  } else if (selected === true) {
    selected = MapMarkerSelectType.Selected;
  }
  let strokeDasharray = '';
  if (selected === MapMarkerSelectType.PartiallySelected) {
    strokeDasharray = ` stroke-dasharray="${width * 2},${width * 4}" `;
  }

  const homeNumberInfo = isHome ? '<span>' + homeNumber + '</span>' : '';
  number = number
    ? '<span>' + number + '</span>'
    : '<span>' +
      '<svg width="24px" fill="' +
      color +
      '" stroke-dasharray="' +
      strokeDasharray +
      '"height="24px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M3 19.723V21a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h12v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1.277A1.99 1.99 0 0 0 22 18v-3c0-.831-.507-1.542-1.228-1.845l-1.368-4.104A2.995 2.995 0 0 0 16.559 7H7.441a2.995 2.995 0 0 0-2.845 2.051l-1.368 4.104A2.001 2.001 0 0 0 2 15v3c0 .738.404 1.376 1 1.723zM5.5 18a1.5 1.5 0 1 1 .001-3.001A1.5 1.5 0 0 1 5.5 18zm13 0a1.5 1.5 0 1 1 .001-3.001A1.5 1.5 0 0 1 18.5 18zM7.441 9h9.117a1 1 0 0 1 .949.684L18.613 13H5.387l1.105-3.316c.137-.409.519-.684.949-.684z"/><path d="M22 7.388V5.279l-9.684-3.228a.996.996 0 0 0-.658.009L2 5.572V7.7l10.015-3.642L22 7.388z"/></svg>' +
      homeNumberInfo +
      '</span>';

  const waypointID = data?.waypointID;
  const html = pointMarkerFigure({ color, figure, id, number, selected, strokeDasharray, width, waypointID });

  return new L.DivIcon({
    iconSize: new L.Point(35, 35),
    className: 'my-div-icon',
    html: html
  });
};

export const updateWidthMarker = (
  element?: HTMLElement | null,
  type?: MarkerFigureWidth,
  selected?: MapMarkerSelectType | string | boolean | undefined
) => {
  if (!element) return element;
  if (element.classList.contains(MarkerFigure.Triangle)) {
    const svgPath = element.firstElementChild?.firstElementChild;
    if (svgPath) {
      const width: 4 | 6 | 8 = type === MarkerFigureWidth.Hover ? 6 : type === MarkerFigureWidth.Select ? 8 : 4;
      svgPath.setAttribute('stroke-width', width.toString());
      if (selected === MapMarkerSelectType.PartiallySelected) {
        svgPath.setAttribute('stroke-dasharray', `"${width * 2},${width * 4}" `);
      } else {
        svgPath.removeAttribute('stroke-dasharray');
      }
    }
  } else {
    switch (type) {
      case MarkerFigureWidth.Hover:
        element.style.borderWidth = `3px`;
        break;
      case MarkerFigureWidth.Select:
        element.style.borderWidth = `4px`;
        break;
      default:
        element.style.borderWidth = `2px`;
        break;
    }
    switch (selected) {
      case MapMarkerSelectType.PartiallySelected:
        element.style.borderStyle = 'dashed';
        break;
      default:
        element.style.borderStyle = 'solid';
        break;
    }
  }
  return;
};

export const locationMarker = () => {
  return new L.DivIcon({
    className: 'my-div-icon',
    html: '<div class="marker-order"></div>'
  });
};

export const colorMarker = (color: string) => {
  return new L.DivIcon({
    className: 'my-div-icon',
    html: '<div class="marker-color" style="background-color:; ' + color + '"></div>'
  });
};
/**
 * Иконка грузовика для маркера
 * @param _deg
 * @param color
 */
export const truckMarker = (_deg: number, color?: string) => {
  const html =
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="${
      color ?? COLOR_BLACK
    }" version="1.1" width="256" height="256" viewBox="0 0 256 256" xml:space="preserve">` +
    '<g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >' +
    '<circle cx="80.71300000000001" cy="60.793" r="5.563" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10;" transform="  matrix(1 0 0 1 0 0) "/>' +
    '<path d="M 89.582 47.937 L 79.276 36.344 c -0.314 -0.353 -0.764 -0.555 -1.237 -0.555 l -10.475 0 c -3.419 8.412 -3.419 16.825 0 25.237 h 5.574 c -0.002 -0.079 -0.012 -0.156 -0.012 -0.235 c 0 -4.19 3.396 -7.586 7.586 -7.586 c 4.19 0 7.586 3.396 7.586 7.586 c 0 0.079 -0.009 0.157 -0.012 0.235 h 0.059 c 0.914 0 1.655 -0.741 1.655 -1.655 V 49.037 C 90 48.631 89.851 48.24 89.582 47.937 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10;  opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />' +
    '<path d="M 63.841 23.646 H 32.209 c -2.056 0 -3.723 2.055 -3.723 4.589 v 30.577 c 0 1.222 0.804 2.213 1.795 2.213 h 3.875 h 1.43 c -0.002 -0.079 -0.012 -0.156 -0.012 -0.235 c 0 -4.19 3.396 -7.586 7.586 -7.586 c 4.19 0 7.586 3.396 7.586 7.586 c 0 0.079 -0.009 0.157 -0.012 0.235 h 16.829 V 45.057 v -8.039 v -8.782 C 67.564 25.701 65.897 23.646 63.841 23.646 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />' +
    '<circle cx="43.163000000000004" cy="60.793" r="5.563" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; " transform="  matrix(1 0 0 1 0 0) "/>' +
    '<path d="M 90 48.407 H 75.454 c -0.817 0 -1.48 -0.662 -1.48 -1.48 v -5.502 c 0 -0.817 0.662 -1.48 1.48 -1.48 l 7.024 0 C 85.428 42.142 87.863 45.065 90 48.407 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />' +
    '</g>' +
    '</svg>';
  return new L.DivIcon({
    iconSize: new L.Point(40, 40),
    className: 'truck-icon',
    html
  });
};

export const carMarker = () => {
  const svg =
    '<svg fill="#fff" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"width="612px" height="612px" viewBox="0 0 612 612" style="enable-background:new 0 0 612 612;" xml:space="preserve">' +
    '<g>' +
    '<path d="M541.322,500.219v-94.372c0-20.277-16.438-36.716-36.715-36.716h-9.598V24.598c0-3.082-1.547-5.958-4.117-7.657L467.587,1.537c-6.103-4.033-14.239,0.342-14.239,7.657v110.652l-6.945-18.734c-9.34-25.196-33.373-41.918-60.245-41.918H225.702c-27.03,0-51.169,16.916-60.394,42.323l-6.655,18.329V9.194c0-7.314-8.137-11.69-14.24-7.657L121.107,16.94c-2.571,1.699-4.118,4.575-4.118,7.657v344.534h-9.597c-20.277,0-36.715,16.438-36.715,36.716v94.372H55.035c-5.069,0-9.178,4.109-9.178,9.179v50.743c0,5.069,4.109,9.179,9.178,9.179h39.598v24.322c0,10.139,8.219,18.357,18.358,18.357h48.645c10.139,0,18.358-8.219,18.358-18.357V569.32h252.014v24.322c0,10.139,8.22,18.357,18.357,18.357h48.646c10.139,0,18.357-8.219,18.357-18.357V569.32h39.598c5.07,0,9.179-4.11,9.179-9.179v-50.742c0-5.07-4.109-9.179-9.179-9.179L541.322,500.219L541.322,500.219z M170.814,170.975h270.372v90.44H170.814V170.975z M164.527,474.533H133.17c-9.581,0-17.348-7.768-17.348-17.349v-0.438c0-9.581,7.767-17.348,17.348-17.348h31.356c9.581,0,17.348,7.767,17.348,17.348v0.438C181.875,466.765,174.108,474.533,164.527,474.533z M368.398,479.648H243.602c-10.139,0-18.358-8.22-18.358-18.357V344.976c0-10.138,8.219-18.357,18.358-18.357h124.796c10.138,0,18.357,8.22,18.357,18.357v116.314C386.756,471.428,378.536,479.648,368.398,479.648z M478.829,474.533h-31.356c-9.58,0-17.348-7.768-17.348-17.349v-0.438c0-9.581,7.768-17.348,17.348-17.348h31.356c9.581,0,17.349,7.767,17.349,17.348v0.438C496.178,466.765,488.41,474.533,478.829,474.533z M365.607,393.801H246.099c-5.019,0-9.087-4.068-9.087-9.088v-0.184c0-5.019,4.068-9.086,9.087-9.086h119.508c5.019,0,9.087,4.067,9.087,9.086v0.184C374.694,389.733,370.626,393.801,365.607,393.801z M365.607,357.085H246.099c-5.019,0-9.087-4.068-9.087-9.087v-0.184c0-5.018,4.068-9.086,9.087-9.086h119.508c5.019,0,9.087,4.068,9.087,9.086v0.184C374.694,353.017,370.626,357.085,365.607,357.085z M365.607,467.232H246.099c-5.019,0-9.087-4.068-9.087-9.087v-0.184c0-5.019,4.068-9.087,9.087-9.087h119.508c5.019,0,9.087,4.068,9.087,9.087v0.184C374.694,463.164,370.626,467.232,365.607,467.232z M365.607,430.516H246.099c-5.019,0-9.087-4.068-9.087-9.086v-0.184c0-5.019,4.068-9.087,9.087-9.087h119.508c5.019,0,9.087,4.068,9.087,9.087v0.184C374.694,426.448,370.626,430.516,365.607,430.516z"/>' +
    '</g>' +
    '</svg>';

  const html = '<div>' + svg + '<div>';
  return new L.DivIcon({
    iconSize: new L.Point(35, 35),
    className: 'car-icon',
    html: html
  });
};
export const waypointMarker = (className?: string, Ind?: number | string) => {
  const svg =
    '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 1000 1000" enable-background="new 0 0 1000 1000" xml:space="preserve">' +
    '<g>' +
    '<path d="M500.2,10c-193,0-331.9,123.5-332.2,319.8C168,542.6,500.2,990,500.2,990S832,540.6,832,329.8C832,135.6,699.3,10,500.2,10z M628.8,291.8c0,71.1-56.7,129-127,129c-70,0-127-57.7-127-129c0-65.2,47.7-119.1,109.7-127.7c7.8-1.8,15.6-2.5,23.8-2.5c66.6,0,120.7,54,120.7,120.5c0,1.1-0.2,1.9-0.2,2.7C628.8,287.4,628.8,289.6,628.8,291.8z"/>' +
    '</g>' +
    '</svg>';

  return new L.DivIcon({
    iconSize: new L.Point(25, 25),
    className: `waypoint-icon${className ? JoinChar.Space + className : EMPTY_STRING}`,
    html: `${svg}<span>${Ind}</span>`
  });
};

export const arrowMarker = (deg: number, color: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" height="35px" width="25px" viewBox="-52 -100 104 153">
      <path d="M -1 -100 L -1 -100 L 52 52 L -1 38 L -52 52 z" stroke="${color}" stroke-width="5" fill="#FFF"/>
    </svg>
    `;

  const html = `<div class="icon-rotate-${Math.round(deg)}">${svg}</div>`;
  return new L.DivIcon({
    iconSize: new L.Point(35, 35),
    className: 'arrow-icon',
    html: html
  });
};

export const eventMarker = (type: MonitoringSensorEventStatusType) => {
  let svg: string;
  let className: string;
  switch (type) {
    case MonitoringSensorEventStatusType.SharpTurn:
      svg = `<svg  viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.790619 2.09314C0.638212 1.98427 0.437745 1.96972 0.271209 2.05542C0.104674 2.14112 0 2.31271 0 2.5V12.5C0 12.6873 0.104674 12.8589 0.271209 12.9446C0.437745 13.0303 0.638212 13.0157 0.790619 12.9069L7 8.4716V12.5C7 12.6873 7.10467 12.8589 7.27121 12.9446C7.43774 13.0303 7.63821 13.0157 7.79062 12.9069L14.7906 7.90687C14.922 7.81301 15 7.66148 15 7.5C15 7.33853 14.922 7.18699 14.7906 7.09314L7.79062 2.09314C7.63821 1.98427 7.43774 1.96972 7.27121 2.05542C7.10467 2.14112 7 2.31271 7 2.5V6.52841L0.790619 2.09314Z"/>
        </svg>`;
      className = 'turn';
      break;
    case MonitoringSensorEventStatusType.SharpBraking:
      svg = `<svg version="1.1" id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512.009 512.009" style="enable-background:new 0 0 512.009 512.009;" xml:space="preserve">
          <g>
            <path d="M277.337,256.007c11.776,0,21.333-9.557,21.333-21.333s-9.557-21.333-21.333-21.333s-21.333,9.557-21.333,21.333
                S265.561,256.007,277.337,256.007z"/>
            <path d="M277.337,298.674c-11.776,0-21.333,9.557-21.333,21.333s9.557,21.333,21.333,21.333s21.333-9.557,21.333-21.333
                S289.113,298.674,277.337,298.674z"/>
            <path d="M277.337,149.34c-70.683,0-128,57.317-128,128s57.317,128,128,128s128-57.317,128-128S348.02,149.34,277.337,149.34z
                M277.337,362.674c-47.119,0-85.333-38.214-85.333-85.333s38.214-85.333,85.333-85.333s85.333,38.214,85.333,85.333
                S324.456,362.674,277.337,362.674z"/>
            <path d="M307.08,44.344V33.679c0-19.406-16.295-34.56-35.517-33.64C124.746,7.211,7.202,124.762,0.054,271.554
                c-0.98,19.189,14.2,35.512,33.617,35.512h10.664c14.711,115.872,113.645,204.943,232.869,204.943
                c129.674,0,234.795-105.128,234.795-234.816C511.999,157.967,422.95,59.046,307.08,44.344z M264.405,43.281v42.914
                c-5.207,0.546-10.35,1.299-15.427,2.23c-60.921,11.163-111.992,49.574-140.217,102.267
                c-11.965,22.334-19.826,47.232-22.597,73.707H43.296C53.479,146.906,146.943,53.453,264.405,43.281z M277.205,469.342
                c-95.683,0-175.418-70.121-189.795-162.261h7.075c12.207,0,23.097-6.537,28.984-16.465c2.605-4.383,4.237-9.426,4.564-14.865
                c1.85-30.291,12.134-58.188,28.543-81.408c22.705-32.124,57.14-55.289,97.251-63.412c7.144-1.445,14.467-2.413,21.935-2.87
                c4.989-0.3,9.65-1.697,13.773-3.941c0.072-0.039,0.141-0.082,0.212-0.122c0.383-0.212,0.764-0.427,1.137-0.654
                c0.252-0.153,0.497-0.317,0.745-0.476c0.188-0.121,0.38-0.237,0.565-0.361c0.316-0.212,0.623-0.435,0.931-0.658
                c0.111-0.081,0.227-0.156,0.337-0.238c0.316-0.235,0.623-0.48,0.93-0.725c0.097-0.077,0.197-0.15,0.293-0.229
                c0.258-0.211,0.506-0.431,0.757-0.649c0.139-0.121,0.283-0.237,0.42-0.36c0.147-0.132,0.286-0.27,0.43-0.405
                c6.661-6.195,10.787-15.058,10.787-24.742v-7.085c92.143,14.361,162.253,94.086,162.253,189.778
                C469.333,383.317,383.314,469.342,277.205,469.342z"/>
            <path d="M234.67,256.007c-11.776,0-21.333,9.557-21.333,21.333s9.557,21.333,21.333,21.333s21.333-9.557,21.333-21.333
                S246.446,256.007,234.67,256.007z"/>
            <path d="M320.004,256.007c-11.776,0-21.333,9.557-21.333,21.333s9.557,21.333,21.333,21.333s21.333-9.557,21.333-21.333
                S331.78,256.007,320.004,256.007z"/>
          </g>
        </svg>`;
      className = 'braking';
      break;

    default:
      svg = `<svg version="1.1" id="Capa_1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="528.919px" height="528.918px" viewBox="0 0 528.919 528.918" style="enable-background:new 0 0 528.919 528.918;" xml:space="preserve">
          <g>
            <path d="M70.846,324.059c3.21,3.926,8.409,3.926,11.619,0l69.162-84.621c3.21-3.926,1.698-7.108-3.372-7.108h-36.723
              c-5.07,0-8.516-4.061-7.427-9.012c18.883-85.995,95.625-150.564,187.207-150.564c105.708,0,191.706,85.999,191.706,191.706
              c0,105.709-85.998,191.707-191.706,191.707c-12.674,0-22.95,10.275-22.95,22.949s10.276,22.949,22.95,22.949
              c131.018,0,237.606-106.588,237.606-237.605c0-131.017-106.589-237.605-237.606-237.605
              c-116.961,0-214.395,84.967-233.961,196.409c-0.878,4.994-5.52,9.067-10.59,9.067H5.057c-5.071,0-6.579,3.182-3.373,7.108
              L70.846,324.059z"/>
          </g>
        </svg>`;
      className = 'acc';
      break;
  }

  const html = svg;
  return new L.DivIcon({
    iconSize: new L.Point(35, 35),
    className: `event-icon ` + className,
    html: html
  });
};

